import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Table, Tooltip } from 'antd';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { QuestionModal } from '../../../../common';
import { normalizeResponseName } from './responseHelpers.js';

/**
 * Editable table of poll responses with search, pagination,
 * bulk delete and bulk add (one response per line).
 */
const ResponsesTable = ({ responses, isUsedInPoll, onChange, onAdd }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [keysToDelete, setKeysToDelete] = useState(null);
  const [bulkText, setBulkText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

  const canDelete = (record) => record.editable && !(isUsedInPoll && record.id);

  const duplicateNames = useMemo(() => {
    const counts = {};
    responses.forEach(({ name }) => {
      const normalized = normalizeResponseName(name);
      if (normalized) counts[normalized] = (counts[normalized] ?? 0) + 1;
    });
    return new Set(Object.keys(counts).filter((name) => counts[name] > 1));
  }, [responses]);

  const filteredResponses = useMemo(() => {
    const needle = normalizeResponseName(search);
    return responses
      .map((response, index) => ({ ...response, order: index + 1 }))
      .filter(
        ({ name }) => !needle || normalizeResponseName(name).includes(needle),
      );
  }, [responses, search]);

  const updateName = (key, name) =>
    onChange(
      responses.map((response) =>
        response.key === key ? { ...response, name } : response,
      ),
    );

  const removeKeys = (keys) => {
    onChange(responses.filter((response) => !keys.includes(response.key)));
    setSelectedKeys(selectedKeys.filter((key) => !keys.includes(key)));
  };

  const addBulk = () => {
    const names = bulkText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (names.length > 0) {
      onAdd(names);
      setBulkText('');
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'order',
      width: 70,
    },
    {
      title: t('Odpoveď'),
      dataIndex: 'name',
      render: (name, record) => {
        const isEmpty = !normalizeResponseName(name);
        const isDuplicate = duplicateNames.has(normalizeResponseName(name));
        const input = (
          <Input
            value={name ?? ''}
            disabled={!record.editable}
            status={isEmpty || isDuplicate ? 'error' : undefined}
            placeholder={isEmpty ? t('Odpoveď nemôže byť prázdna.') : undefined}
            onChange={(e) => updateName(record.key, e.target.value)}
          />
        );

        if (!record.editable) {
          return (
            <Tooltip
              title={t(
                'Pre túto odpoveď už hlasovali užívatelia, preto ju nie je možné upraviť ani vymazať.',
              )}
            >
              {input}
            </Tooltip>
          );
        }
        if (isDuplicate) {
          return (
            <Tooltip title={t('Každá odpoveď musí byť unikátna.')}>
              {input}
            </Tooltip>
          );
        }
        return input;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <IconButton
          icon={
            <DeleteFilled style={{ color: canDelete(record) ? '' : 'gray' }} />
          }
          tooltip={t('Vymazať')}
          disabled={!canDelete(record)}
          disabledTooltip={t('Nie je možné vymazať odpoveď.')}
          onClick={() => setKeysToDelete([record.key])}
        />
      ),
    },
  ];

  return (
    <div className="responses-table">
      <div className="responses-table-toolbar">
        <Input.Search
          allowClear
          placeholder={t('Hľadať odpoveď')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination({ ...pagination, current: 1 });
          }}
        />
        <span className="responses-table-count">
          {t('Počet odpovedí')}: <b>{responses.length}</b>
        </span>
        <Button
          danger
          icon={<DeleteFilled />}
          disabled={selectedKeys.length === 0}
          onClick={() => setKeysToDelete(selectedKeys)}
        >
          {t('Vymazať vybrané')} ({selectedKeys.length})
        </Button>
      </div>

      <Table
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={filteredResponses}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (record) => ({ disabled: !canDelete(record) }),
        }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
        }}
        locale={{ emptyText: t('Zatiaľ nie sú zadané žiadne odpovede.') }}
      />

      <div className="responses-table-bulk-add">
        <Input.TextArea
          rows={4}
          value={bulkText}
          placeholder={t('Nové odpovede, každá na samostatný riadok')}
          onChange={(e) => setBulkText(e.target.value)}
        />
        <Button
          icon={<PlusOutlined />}
          disabled={!bulkText.trim()}
          onClick={addBulk}
        >
          {t('Pridať odpovede')}
        </Button>
      </div>

      <QuestionModal
        chosenPositive={() => removeKeys(keysToDelete)}
        question={
          keysToDelete?.length > 1
            ? t('Naozaj chcete vymazať vybrané položky?') +
              ` (${keysToDelete.length})`
            : t('Naozaj chcete vymazať túto položku?')
        }
        visible={keysToDelete !== null}
        yes={t('Áno')}
        toggle={() => setKeysToDelete(null)}
        headerTitle={t('Vymazať položku')}
      />
    </div>
  );
};

ResponsesTable.propTypes = {
  responses: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      id: PropTypes.number,
      name: PropTypes.string,
      editable: PropTypes.bool,
    }),
  ).isRequired,
  isUsedInPoll: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default ResponsesTable;
